/**
 * Reports.js  –  Management Reports for "solution" / "owner" role
 *
 * Tabs:
 *   1. Appointments  – session counts, completion status, auto-marked, missed
 *   2. Therapist Work – per-therapist breakdown of sessions, revenue, auto-marks
 *   3. Patients       – active, renewals pending, missed streak
 *   4. Finance        – revenue, bills, expenses, balance
 *   5. Payroll        – staff payroll records
 *   6. Attendance     – staff attendance per period
 *   7. HR / Leaves    – leave requests
 *   8. Security       – audit logs / history
 *
 *   CL Reports + OTT Reports → moved to MyReports.js
 */

import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import "./Reports.css";

/* ─── Date helpers ───────────────────────────────────────────────── */
function todayStr() { return new Date().toISOString().slice(0, 10); }
function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function fmt(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}
function fmtNum(n) { return (n ?? 0).toLocaleString("en-IN"); }
function rupee(n) { return "₹" + fmtNum(Math.round(n ?? 0)); }

const TABS = [
  { id: "appointments", label: "📅 Appointments" },
  { id: "therapist",    label: "🧑‍⚕️ Therapist Work" },
  { id: "patients",     label: "🧍 Patients" },
  { id: "finance",      label: "💰 Finance" },
  { id: "payroll",      label: "📑 Payroll" },
  { id: "attendance",   label: "🕐 Attendance" },
  { id: "leaves",       label: "🌿 Leaves" },
  { id: "security",     label: "🔒 Security" },
];

export default function Reports() {
  const [activeTab, setActiveTab] = useState("appointments");
  const [from, setFrom]   = useState(monthStart());
  const [to,   setTo]     = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  /* ── per-tab data ── */
  const [appointments, setAppointments] = useState([]);
  const [patients,     setPatients]     = useState([]);
  const [staff,        setStaff]        = useState([]);
  const [expenses,     setExpenses]     = useState({ items: [], balance: 0 });
  const [bills,        setBills]        = useState([]);
  const [leaves,       setLeaves]       = useState([]);
  const [auditLogs,    setAuditLogs]    = useState([]);
  const [payrollList,  setPayrollList]  = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});

  /* ── fetch helpers ── */
  const fetchTab = useCallback(async (tab) => {
    setLoading(true); setError("");
    try {
      switch (tab) {
        case "appointments":
        case "therapist": {
          const r = await api.get(
            `${process.env.REACT_APP_URL}/appointments?from=${from}&to=${to}`
          ).catch(() => ({ data: [] }));
          setAppointments(r.data || []);
          break;
        }
        case "patients": {
          const r = await api.get(`${process.env.REACT_APP_URL}/patients/profiles`)
            .catch(() => ({ data: [] }));
          setPatients(r.data || []);
          break;
        }
        case "finance": {
          const [billsRes, expRes, apptRes] = await Promise.all([
            api.get(`${process.env.REACT_APP_URL}/bills/by-date?from=${from}&to=${to}`).catch(() => ({ data: [] })),
            api.get(`${process.env.REACT_APP_URL}/expenses`).catch(() => ({ data: {} })),
            api.get(`${process.env.REACT_APP_URL}/appointments?from=${from}&to=${to}`).catch(() => ({ data: [] })),
          ]);
          setBills(Array.isArray(billsRes.data) ? billsRes.data : []);
          setAppointments(apptRes.data || []);
          const expData = expRes.data;
          // expenses endpoint returns { expenses: [...], page, totalCount, ... }
          const expItems = Array.isArray(expData?.expenses) ? expData.expenses
            : Array.isArray(expData) ? expData : [];
          const expBalance = expItems.length > 0
            ? (expItems[0].balanceAfterTxn || 0)  // sorted desc, first = latest balance
            : 0;
          setExpenses({ items: expItems, balance: expBalance });
          break;
        }
        case "payroll": {
          const r = await api.get(`${process.env.REACT_APP_URL}/payroll/staff/all`);
          setPayrollList(r.data || []);
          break;
        }
        case "attendance": {
          const staffRes = await api.get(`${process.env.REACT_APP_URL}/payroll/staff/all`);
          const staffList = staffRes.data || [];
          setStaff(staffList);
          // fetch attendance per staff for the period
          const month = from.slice(0, 7); // YYYY-MM
          const entries = await Promise.all(
            staffList.slice(0, 30).map(s =>
              api.get(`${process.env.REACT_APP_URL}/payroll/attendance/${s.userId || s._id}`)
                .then(r => ({ userId: s.userId || s._id, name: s.name || s.username, records: r.data || [] }))
                .catch(() => ({ userId: s.userId || s._id, name: s.name || s.username, records: [] }))
            )
          );
          const map = {};
          entries.forEach(e => { map[e.userId] = e; });
          setAttendanceMap(map);
          break;
        }
        case "leaves": {
          const r = await api.get(
            `${process.env.REACT_APP_URL}/appointments/type/Leave/all?from=${from}&to=${to}`
          ).catch(() => ({ data: [] }));
          setLeaves(Array.isArray(r.data) ? r.data : []);
          break;
        }
        case "security": {
          const r = await api.get(`${process.env.REACT_APP_URL}/history`).catch(() => ({ data: [] }));
          setAuditLogs(Array.isArray(r.data) ? r.data : []);
          break;
        }
        default: break;
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchTab(activeTab); }, [activeTab, fetchTab]);

  /* ─── derived stats ───────────────────────────────────────────── */
  const therapyAppts = appointments.filter(a => a.type === "Therapy");
  const completedAppts = therapyAppts.filter(a => a.status?.toLowerCase() === "completed");
  const inProgressAppts = therapyAppts.filter(a => a.status?.toLowerCase() === "inprogress");
  const scheduledAppts = therapyAppts.filter(a => a.status?.toLowerCase() === "scheduled");
  const autoMarkedAppts = completedAppts.filter(a => a.autoMarkedDone);
  const totalRevenue = completedAppts.reduce((s, a) => s + (a.visitCharges || 0), 0);
  const totalExpense = expenses.items.reduce((s, e) => s + (e.amount || 0), 0);

  // per-therapist breakdown
  const byTherapist = {};
  completedAppts.forEach(a => {
    const t = a.leadTherapist || a.username || "Unknown";
    if (!byTherapist[t]) byTherapist[t] = { sessions: 0, revenue: 0, autoMarked: 0 };
    byTherapist[t].sessions++;
    byTherapist[t].revenue += a.visitCharges || 0;
    if (a.autoMarkedDone) byTherapist[t].autoMarked++;
  });

  /* ─── export helpers ──────────────────────────────────────────── */
  function exportCSV(rows, headers, filename) {
    const lines = [headers.join(","), ...rows.map(r => r.map(c => `"${c ?? ""}"`).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename + ".csv";
    a.click(); URL.revokeObjectURL(url);
  }

  /* ─── tab renderers ───────────────────────────────────────────── */
  function renderAppointments() {
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Total Therapy" value={fmtNum(therapyAppts.length)} />
          <Kpi label="Completed" value={fmtNum(completedAppts.length)} color="#388e3c" />
          <Kpi label="In Progress" value={fmtNum(inProgressAppts.length)} color="#f57c00" />
          <Kpi label="Scheduled" value={fmtNum(scheduledAppts.length)} color="#1976d2" />
          <Kpi label="Auto-Marked" value={fmtNum(autoMarkedAppts.length)} color="#7b1fa2" />
          <Kpi label="Revenue" value={rupee(totalRevenue)} color="#388e3c" />
        </div>

        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Appointments List</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              therapyAppts.map(a => [a.title, a.username, a.leadTherapist || "—", fmt(a.start || a.date), a.status, a.visitCharges || 0, a.autoMarkedDone ? "Yes" : "No", a.sessionComments || ""]),
              ["Patient", "Assigned To", "Lead Therapist", "Date", "Status", "Charges", "Auto-Marked", "Comments"],
              `appointments_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead>
              <tr>
                <th>Patient</th><th>Therapist</th><th>Lead</th>
                <th>Date</th><th>Status</th><th>Charges</th><th>Auto?</th>
              </tr>
            </thead>
            <tbody>
              {therapyAppts.map((a, i) => (
                <tr key={a._id || i} className={a.autoMarkedDone ? "rpt-row-warn" : ""}>
                  <td>{a.title?.split("/")?.[0]?.trim() || a.name || "—"}</td>
                  <td>{a.username}</td>
                  <td>{a.leadTherapist || "—"}</td>
                  <td>{fmt(a.start || a.date)}</td>
                  <td><span className={`rpt-badge rpt-badge-${a.status?.toLowerCase()}`}>{a.status}</span></td>
                  <td>{rupee(a.visitCharges)}</td>
                  <td>{a.autoMarkedDone ? "🤖" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {therapyAppts.length === 0 && <div className="rpt-empty">No appointments in this period</div>}
        </div>
      </div>
    );
  }

  function renderTherapist() {
    const rows = Object.entries(byTherapist).sort((a, b) => b[1].sessions - a[1].sessions);
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Active Therapists" value={fmtNum(rows.length)} />
          <Kpi label="Total Sessions" value={fmtNum(completedAppts.length)} />
          <Kpi label="Total Revenue" value={rupee(totalRevenue)} color="#388e3c" />
          <Kpi label="Auto-Marked" value={fmtNum(autoMarkedAppts.length)} color="#7b1fa2" />
        </div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Per-Therapist Breakdown</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              rows.map(([t, d]) => [t, d.sessions, rupee(d.revenue), d.autoMarked]),
              ["Therapist", "Sessions", "Revenue", "Auto-Marked Sessions"],
              `therapist_work_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead>
              <tr><th>Therapist</th><th>Sessions</th><th>Revenue</th><th>Auto-Marked</th><th>% Auto</th></tr>
            </thead>
            <tbody>
              {rows.map(([therapist, data]) => (
                <tr key={therapist}>
                  <td>{therapist}</td>
                  <td>{fmtNum(data.sessions)}</td>
                  <td>{rupee(data.revenue)}</td>
                  <td>{data.autoMarked > 0 ? <span style={{color:"#7b1fa2"}}>🤖 {data.autoMarked}</span> : "0"}</td>
                  <td>{data.sessions > 0 ? Math.round(data.autoMarked / data.sessions * 100) + "%" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && <div className="rpt-empty">No completed sessions in this period</div>}
        </div>
      </div>
    );
  }

  function renderPatients() {
    const active = patients.filter(p => p.liveStatus && !["Inactive", "Discharged"].includes(p.overallStatusSoFar));
    const renewalPending = patients.filter(p => p.overallStatusSoFar === "Sessions Renewal Pending");
    const noPendingVisits = patients.filter(p => (p.pendingvisits || 0) <= 0);
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Total Patients" value={fmtNum(patients.length)} />
          <Kpi label="Active" value={fmtNum(active.length)} color="#388e3c" />
          <Kpi label="Renewal Pending" value={fmtNum(renewalPending.length)} color="#e53935" />
          <Kpi label="Sessions Exhausted" value={fmtNum(noPendingVisits.length)} color="#f57c00" />
        </div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Patient Status</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              patients.map(p => [p.patientCard, p.name, p.number, p.overallStatusSoFar, p.pendingvisits ?? 0, p.doctor, fmt(p.lastvisitdate)]),
              ["Card", "Name", "Phone", "Status", "Pending Visits", "Doctor", "Last Visit"],
              `patients_${todayStr()}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead>
              <tr><th>Card</th><th>Name</th><th>Phone</th><th>Status</th><th>Pending</th><th>Last Visit</th></tr>
            </thead>
            <tbody>
              {patients.map((p, i) => (
                <tr key={p._id || i} className={renewalPending.includes(p) ? "rpt-row-warn" : ""}>
                  <td>{p.patientCard}</td>
                  <td>{p.name}</td>
                  <td>{p.number || "—"}</td>
                  <td><span className={`rpt-badge rpt-badge-${(p.overallStatusSoFar||"").toLowerCase().replace(/\s+/g,"-")}`}>{p.overallStatusSoFar || "—"}</span></td>
                  <td style={{ color: (p.pendingvisits || 0) <= 0 ? "#e53935" : "inherit" }}>{p.pendingvisits ?? "—"}</td>
                  <td>{fmt(p.lastvisitdate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {patients.length === 0 && <div className="rpt-empty">No patient data</div>}
        </div>
      </div>
    );
  }

  function renderFinance() {
    const totalBilled = bills.reduce((s, b) => s + (b.total || 0), 0);
    const totalPaid   = bills.reduce((s, b) => s + (b.paid  || 0), 0);
    const outstanding = totalBilled - totalPaid;
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Session Revenue" value={rupee(totalRevenue)} color="#388e3c" />
          <Kpi label="Bills Raised" value={rupee(totalBilled)} color="#1976d2" />
          <Kpi label="Collected" value={rupee(totalPaid)} color="#388e3c" />
          <Kpi label="Outstanding" value={rupee(outstanding)} color="#e53935" />
          <Kpi label="Expenses" value={rupee(totalExpense)} color="#f57c00" />
          <Kpi label="Balance" value={rupee(expenses.balance)} color={expenses.balance >= 0 ? "#388e3c" : "#e53935"} />
        </div>

        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Bills</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              bills.map(b => [b.patientCard, b.name || "—", fmt(b.invoicedate || b.createdAt), b.total || 0, b.paid || 0, (b.total||0)-(b.paid||0), b.status || "—"]),
              ["Card", "Patient", "Date", "Total", "Paid", "Outstanding", "Status"],
              `bills_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Patient</th><th>Date</th><th>Total</th><th>Paid</th><th>Outstanding</th><th>Status</th></tr></thead>
            <tbody>
              {bills.map((b, i) => (
                <tr key={b._id || i}>
                  <td>{b.name || b.patientCard || "—"}</td>
                  <td>{fmt(b.invoicedate || b.createdAt)}</td>
                  <td>{rupee(b.total)}</td>
                  <td>{rupee(b.paid)}</td>
                  <td style={{ color: (b.total||0)-(b.paid||0) > 0 ? "#e53935":"inherit" }}>{rupee((b.total||0)-(b.paid||0))}</td>
                  <td>{b.billorg || b.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {bills.length === 0 && <div className="rpt-empty">No bills in this period</div>}
        </div>

        <div className="rpt-table-wrap" style={{ marginTop: 20 }}>
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Expenses</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              expenses.items.map(e => [fmt(e.date || e.createdAt), e.type || "—", e.description || "—", e.amount || 0, e.balanceAfterTxn || 0]),
              ["Date", "Type", "Description", "Amount", "Balance After"],
              `expenses_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Date</th><th>Type</th><th>Description</th><th>Amount</th><th>Balance After</th></tr></thead>
            <tbody>
              {expenses.items.map((e, i) => (
                <tr key={e._id || i}>
                  <td>{fmt(e.date || e.createdAt)}</td>
                  <td><span className={`rpt-badge rpt-badge-${(e.type||"").toLowerCase()}`}>{e.type || "—"}</span></td>
                  <td>{e.description || "—"}</td>
                  <td style={{ color: e.type === 'credit' ? '#388e3c' : '#e53935' }}>{rupee(e.amount)}</td>
                  <td>{rupee(e.balanceAfterTxn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {expenses.items.length === 0 && <div className="rpt-empty">No expense records</div>}
        </div>
      </div>
    );
  }

  function renderPayroll() {
    return (
      <div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Staff Payroll Records</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              payrollList.map(p => [p.name || p.username || p.userId, p.designation || "—", p.department || "—", p.salary || 0, p.status || "—"]),
              ["Name", "Designation", "Department", "Salary", "Status"],
              `payroll_${todayStr()}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Name</th><th>User ID</th><th>Designation</th><th>Department</th><th>Salary</th><th>Status</th></tr></thead>
            <tbody>
              {payrollList.map((p, i) => (
                <tr key={p._id || i}>
                  <td>{p.name || p.username || "—"}</td>
                  <td>{p.userId || "—"}</td>
                  <td>{p.designation || "—"}</td>
                  <td>{p.department || "—"}</td>
                  <td>{rupee(p.salary)}</td>
                  <td>{p.status || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {payrollList.length === 0 && <div className="rpt-empty">No payroll data</div>}
        </div>
      </div>
    );
  }

  function renderAttendance() {
    const entries = Object.values(attendanceMap);
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Staff Tracked" value={fmtNum(entries.length)} />
        </div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Attendance Summary</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              entries.map(e => [e.name, e.records.filter(r => r.checkIn).length, e.records.filter(r => !r.checkIn).length]),
              ["Staff", "Present Days", "Absent Days"],
              `attendance_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Staff</th><th>User ID</th><th>Present</th><th>Late</th><th>Records</th></tr></thead>
            <tbody>
              {entries.map((e, i) => {
                const present = e.records.filter(r => r.checkIn).length;
                const late = e.records.filter(r => r.isLate).length;
                return (
                  <tr key={e.userId || i}>
                    <td>{e.name}</td>
                    <td>{e.userId}</td>
                    <td style={{ color: present > 0 ? "#388e3c" : "#e53935" }}>{present}</td>
                    <td style={{ color: late > 0 ? "#f57c00" : "inherit" }}>{late}</td>
                    <td>{e.records.length}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {entries.length === 0 && <div className="rpt-empty">No attendance data</div>}
        </div>
      </div>
    );
  }

  function renderLeaves() {
    const leaveRows = leaves.filter(a => a.type === "Leave");
    const byUser = {};
    leaveRows.forEach(l => {
      const u = l.username || "Unknown";
      if (!byUser[u]) byUser[u] = 0;
      byUser[u]++;
    });
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Leave Requests" value={fmtNum(leaveRows.length)} />
          <Kpi label="Approved" value={fmtNum(leaveRows.filter(l => l.approved === "Approved").length)} color="#388e3c" />
          <Kpi label="Pending" value={fmtNum(leaveRows.filter(l => !l.approved || l.approved === "Pending").length)} color="#f57c00" />
          <Kpi label="Rejected" value={fmtNum(leaveRows.filter(l => l.approved === "Rejected").length)} color="#e53935" />
        </div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Leave Requests</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              leaveRows.map(l => [l.username, fmt(l.start || l.date), fmt(l.end), l.approved || "Pending", l.sessionComments || ""]),
              ["Staff", "From", "To", "Status", "Reason"],
              `leaves_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Staff</th><th>From</th><th>To</th><th>Status</th><th>Reason</th></tr></thead>
            <tbody>
              {leaveRows.map((l, i) => (
                <tr key={l._id || i}>
                  <td>{l.username}</td>
                  <td>{fmt(l.start || l.date)}</td>
                  <td>{fmt(l.end)}</td>
                  <td><span className={`rpt-badge rpt-badge-${(l.approved||"pending").toLowerCase()}`}>{l.approved || "Pending"}</span></td>
                  <td>{l.sessionComments || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {leaveRows.length === 0 && <div className="rpt-empty">No leave records in this period</div>}
        </div>
      </div>
    );
  }

  function renderSecurity() {
    return (
      <div>
        <div className="rpt-summary-row">
          <Kpi label="Log Entries" value={fmtNum(auditLogs.length)} />
        </div>
        <div className="rpt-table-wrap">
          <div className="rpt-table-toolbar">
            <span className="rpt-table-title">Audit / History Logs</span>
            <button className="rpt-export-btn" onClick={() => exportCSV(
              auditLogs.map(l => [fmt(l.timestamp || l.createdAt), l.username || "—", l.action || "—", l.endpoint || "—", l.status || "—", l.ipAddress || "—"]),
              ["Timestamp", "User", "Action", "Endpoint", "Status", "IP"],
              `audit_${from}_${to}`
            )}>⬇ Export CSV</button>
          </div>
          <table className="rpt-table">
            <thead><tr><th>Time</th><th>User</th><th>Action</th><th>Endpoint</th><th>Status</th><th>IP</th></tr></thead>
            <tbody>
              {auditLogs.slice(0, 200).map((l, i) => (
                <tr key={l._id || i} className={l.status === "failed" ? "rpt-row-warn" : ""}>
                  <td>{fmt(l.timestamp || l.createdAt)}</td>
                  <td>{l.username || "—"}</td>
                  <td><span className={`rpt-badge rpt-badge-${(l.action||"").toLowerCase()}`}>{l.action}</span></td>
                  <td style={{ fontSize: 11 }}>{l.endpoint || "—"}</td>
                  <td>{l.status || "—"}</td>
                  <td>{l.ipAddress || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {auditLogs.length === 0 && <div className="rpt-empty">No audit logs available</div>}
        </div>
      </div>
    );
  }


  const tabRenderers = {
    appointments: renderAppointments,
    therapist:    renderTherapist,
    patients:     renderPatients,
    finance:      renderFinance,
    payroll:      renderPayroll,
    attendance:   renderAttendance,
    leaves:       renderLeaves,
    security:     renderSecurity,
  };

  const needsDateRange = ["appointments", "therapist", "finance", "leaves"].includes(activeTab);

  return (
    <div className="rpt-root">
      <div className="rpt-header">
        <h2 className="rpt-title">📊 Management Reports</h2>
        {needsDateRange && (
          <div className="rpt-date-row">
            <label>From <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
            <label>To <input type="date" value={to}   onChange={e => setTo(e.target.value)} /></label>
            <button className="rpt-refresh-btn" onClick={() => fetchTab(activeTab)}>🔄 Refresh</button>
          </div>
        )}
      </div>

      <div className="rpt-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`rpt-tab${activeTab === t.id ? " active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rpt-body">
        {loading && <div className="rpt-loading">Loading…</div>}
        {error   && <div className="rpt-error">⚠️ {error}</div>}
        {!loading && !error && tabRenderers[activeTab]?.()}
      </div>
    </div>
  );
}

/* ─── KPI card ─────────────────────────────────────────────────── */
function Kpi({ label, value, color }) {
  return (
    <div className="rpt-kpi">
      <div className="rpt-kpi-val" style={{ color: color || "#222" }}>{value}</div>
      <div className="rpt-kpi-label">{label}</div>
    </div>
  );
}
